import { useState } from 'react'
import { Link } from 'react-router-dom'
import Logo from './Logo'
import { useAuth } from '../hooks/useAuth'

// ─── Top nav ──────────────────────────────────────────────────────────────────
// Used on the homepage and all other public marketing pages (Features, Terms,
// Privacy, FAQ, Contact, etc).
//
// Auth-aware: signed-in users see "Dashboard" in place of "Sign in /
// Start free trial". Lets signed-in users visit the marketing site
// (to share links with colleagues, see updates, etc) without the nav
// pushing CTAs that don't apply to them.

export function TopNav() {
  const [open, setOpen] = useState(false)
  const { user } = useAuth()

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(247, 246, 242, 0.85)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid var(--surface-2)',
    }}>
      <div className="container" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 24px',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink)' }}>
          <Logo variant="icon" size={32} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--ink)' }}>
            Receipt Chaser
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <Link to="/features" style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', fontWeight: 500 }}>Features</Link>
          <Link to="/pricing" style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', fontWeight: 500 }}>Pricing</Link>
          <Link to="/faq" style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', fontWeight: 500 }}>FAQ</Link>
          <Link to="/contact" style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', fontWeight: 500 }}>Contact</Link>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary">Dashboard →</Link>
          ) : (
            <>
              <Link to="/login" style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', fontWeight: 500 }}>Sign in</Link>
              <Link to="/signup" className="btn btn-primary">Start free trial</Link>
            </>
          )}
        </nav>

        {/* Mobile burger */}
        <button
          className="mobile-only"
          onClick={() => setOpen(o => !o)}
          aria-label="Menu"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '1.4rem', color: 'var(--ink)',
          }}
        >
          {open ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="mobile-only-block" style={{
          background: 'var(--white)', borderTop: '1px solid var(--surface-2)',
          padding: '14px 24px',
        }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Link onClick={() => setOpen(false)} to="/features" style={{ color: 'var(--ink)', fontSize: '0.95rem' }}>Features</Link>
            <Link onClick={() => setOpen(false)} to="/pricing" style={{ color: 'var(--ink)', fontSize: '0.95rem' }}>Pricing</Link>
            <Link onClick={() => setOpen(false)} to="/faq" style={{ color: 'var(--ink)', fontSize: '0.95rem' }}>FAQ</Link>
            <Link onClick={() => setOpen(false)} to="/contact" style={{ color: 'var(--ink)', fontSize: '0.95rem' }}>Contact</Link>
            {user ? (
              <Link onClick={() => setOpen(false)} to="/dashboard" className="btn btn-primary" style={{ marginTop: 6 }}>Dashboard →</Link>
            ) : (
              <>
                <Link onClick={() => setOpen(false)} to="/login" style={{ color: 'var(--ink)', fontSize: '0.95rem' }}>Sign in</Link>
                <Link onClick={() => setOpen(false)} to="/signup" className="btn btn-primary" style={{ marginTop: 6 }}>Start free trial</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────

export function Footer() {
  const { user } = useAuth()
  return (
    <footer style={{ padding: '48px 0 32px', background: 'var(--surface)', borderTop: '1px solid var(--surface-2)' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 32, marginBottom: 32,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Logo variant="icon" size={26} />
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>Receipt Chaser</span>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', lineHeight: 1.55 }}>
              Receipt and invoice collection for UK bookkeepers.
            </p>
          </div>

          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Product
            </div>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li><Link to="/features" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>How it works</Link></li>
              <li><Link to="/features" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Features</Link></li>
              <li><Link to="/pricing" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Pricing</Link></li>
              <li><Link to="/faq" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>FAQ</Link></li>
            </ul>
          </div>

          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Company
            </div>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li><Link to="/articles" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Articles</Link></li>
              <li><Link to="/contact" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Contact</Link></li>
              <li><Link to="/terms" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Terms &amp; conditions</Link></li>
              <li><Link to="/privacy" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Privacy policy</Link></li>
            </ul>
          </div>

          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Account
            </div>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {user ? (
                <li><Link to="/dashboard" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Dashboard</Link></li>
              ) : (
                <>
                  <li><Link to="/login" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Sign in</Link></li>
                  <li><Link to="/signup" style={{ color: 'var(--ink-muted)', fontSize: '0.88rem' }}>Start a free trial</Link></li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div style={{
          paddingTop: 24, borderTop: '1px solid var(--surface-2)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
        }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
            © {new Date().getFullYear()} Receipt Chaser. All rights reserved.
          </p>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
            Made in the UK
          </p>
        </div>
      </div>
    </footer>
  )
}
